import { type z } from 'zod';
import { string as __eliadmin_zod_0, object as __eliadmin_zod_1, boolean as __eliadmin_zod_2 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();

import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { isValidPhone, splitStreet } from '@eliadmin/core/src/utils/nt/address';

const text = __eliadmin_zod_0().trim();

const profileFields = __eliadmin_zod_1({
  name: text,
  surname: text,
  /** The login – shown read-only, sent unchanged like the core profile form. */
  email: __eliadmin_zod_0(),
  phone: text,
  company: text,
  ico: text,
  dic: text,
  ic_dph: text,
  /** „Ulica a číslo domu“ in one field; the update splits it (`splitStreet`). */
  address: text,
  city: text,
  postcode: text,
  /** Country id. */
  country: text,
  same_delivery: __eliadmin_zod_2(),
  delivery_address: text,
  delivery_city: text,
  delivery_postcode: text,
  delivery_country: text,
});

/**
 * „Môj profil“ as one flat form. The design marks every field required (the profile feeds the next
 * orders) – the same rules as the checkout form (`checkout-schema.ts`): a street needs its house
 * number, the phone E.164-valid, the company identification for a company customer only, the
 * delivery address only while it differs from the billing one.
 */
export const profileSchema = (
  VM: FormValidationMessages,
  isCompany: boolean,
  /** „Objednávka - chýba číslo domu“ (admin); the core „can't be empty“ message as a fallback. */
  streetNumberMissing = VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
) =>
  profileFields.superRefine((values, ctx) => {
    const issue = (path: keyof typeof values, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    const required = (...paths: (keyof typeof values)[]) => {
      for (const path of paths) {
        if (!values[path]) issue(path, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      }
    };
    const street = (path: 'address' | 'delivery_address') => {
      if (!values[path]) issue(path, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE());
      else if (!splitStreet(values[path]).number) issue(path, streetNumberMissing);
    };

    required('name', 'surname', 'city', 'postcode', 'country');
    street('address');
    if (!values.phone) issue('phone', VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE());
    else if (!isValidPhone(values.phone)) issue('phone', VM.PHONE_INVALID_VALIDATION_MESSAGE());

    if (isCompany) required('company', 'ico', 'dic');
    if (!values.same_delivery) {
      street('delivery_address');
      required('delivery_city', 'delivery_postcode', 'delivery_country');
    }
  });

export type ProfileFormInput = z.input<typeof profileFields>;
export type ProfileFormValues = z.output<typeof profileFields>;
